<script setup>
import {computed, ref} from "vue";
import PlayerCard from "~/components/players/PlayerCard.vue";
import StatsFormTable from "~/components/stats/StatsFormTable.vue";
import {useStatConverters} from "~/composables/useStatConverters";

// Self-contained leaderboard view. Give it the raw realtimeStats rows and a
// form mode; it owns everything else (period/all-time tables, metric picker,
// sort direction). 'recent' shows the 1/3/6/12-month form; 'allTime' shows a
// single all-time "Form" table in the same format.
const props = defineProps({
  stats: {type: Array, required: true},
  formMode: {type: String, default: 'recent'}, // 'recent' | 'allTime'
});

const {t} = useI18n();
const {doubleToPercentage} = useStatConverters();

const isAllTime = computed(() => props.formMode === 'allTime');

/* ── Recent form: top-5 leaderboard per period ── */
const formRows = (winsKey, winPercentageKey, totalKey) => {
  if (!props.stats?.length) return [];
  return [...props.stats]
      .sort((a, b) => {
        if (b[winsKey] !== a[winsKey]) return b[winsKey] - a[winsKey];
        if (b[winPercentageKey] !== a[winPercentageKey]) return b[winPercentageKey] - a[winPercentageKey];
        return b[totalKey] - a[totalKey];
      })
      .slice(0, 5)
      .map(s => ({id: s.id, player: s.player, wins: s[winsKey], total: s[totalKey], percentage: s[winPercentageKey]}));
};

// Each period: chip label (mobile), full label (2x2 grid heading), rows.
const periods = computed(() => [
  {key: '1Month', label: '1m', title: 'Last month', rows: formRows('wins1Month', 'winPercentage1Month', 'total1Month')},
  {key: '3Months', label: '3m', title: 'Last 3 months', rows: formRows('wins3Months', 'winPercentage3Months', 'total3Months')},
  {key: '6Months', label: '6m', title: 'Last 6 months', rows: formRows('wins6Months', 'winPercentage6Months', 'total6Months')},
  {key: '12Months', label: '12m', title: 'Last 12 months', rows: formRows('wins12Months', 'winPercentage12Months', 'total12Months')},
]);

// Mobile single-period selection (default 3 months, matching Flutter).
const period = ref('3Months');
const activePeriod = computed(() => periods.value.find(p => p.key === period.value) || periods.value[0]);

// All-time form (leagues/tournaments).
const allTimeData = computed(() => formRows('wins', 'winPercentage', 'totalFixtures'));

/* ── All stats: metric picker + sortable bars ── */
const selectedMetric = ref('tapLeaguesScore');
const sortDirection = ref('Descending');

const metricOptions = computed(() => [
  // Headline first: where you stand, then what you have banked.
  {title: 'Tap Leagues Rating', value: 'tapLeaguesScore', percentage: false},
  {title: 'Total points awarded', value: 'totalPoints', percentage: false},
  {title: 'Total points conceded', value: 'totalPointsConceded', percentage: false},
  {title: 'Wins', value: 'wins', percentage: false},
  {title: 'Win Percentage', value: 'winPercentage', percentage: true},
  {title: t("total_fixtures"), value: 'totalFixtures', percentage: false},
  // Recent form, nearest window first.
  {title: 'Wins (Last Month)', value: 'wins1Month', percentage: false},
  {title: 'Win Percentage (Last Month)', value: 'winPercentage1Month', percentage: true},
  {title: t("total_fixtures_last_month"), value: 'total1Month', percentage: false},
  {title: 'Wins (Last 3 Months)', value: 'wins3Months', percentage: false},
  {title: 'Win Percentage (Last 3 Months)', value: 'winPercentage3Months', percentage: true},
  {title: t("total_fixtures_last_x_months", {months: 3}), value: 'total3Months', percentage: false},
  {title: 'Wins (Last 6 Months)', value: 'wins6Months', percentage: false},
  {title: 'Win Percentage (Last 6 Months)', value: 'winPercentage6Months', percentage: true},
  {title: t("total_fixtures_last_x_months", {months: 6}), value: 'total6Months', percentage: false},
  {title: 'Wins (Last 12 Months)', value: 'wins12Months', percentage: false},
  {title: 'Win Percentage (Last 12 Months)', value: 'winPercentage12Months', percentage: true},
  {title: t("total_fixtures_last_x_months", {months: 12}), value: 'total12Months', percentage: false},
  // Streaks.
  {title: 'Current Win Streak', value: 'currentWinStreak', percentage: false},
  {title: 'Max Win Streak', value: 'maxWinStreak', percentage: false},
  {title: 'Current Loss Streak', value: 'currentLossStreak', percentage: false},
  {title: 'Max Loss Streak', value: 'maxLossStreak', percentage: false},
  // Points detail.
  {title: 'Points from wins', value: 'pointsAwarded', percentage: false},
  {title: 'Points conceded in losses', value: 'pointsConceded', percentage: false},
  {title: t("average_fixture_points_margin"), value: 'averageFixturePointsMargin', percentage: false},
  // Losses and draws — the tail.
  {title: 'Losses', value: 'losses', percentage: false},
  {title: 'Loss Percentage', value: 'lossPercentage', percentage: true},
  {title: 'Draws', value: 'draws', percentage: false},
  {title: 'Draw Percentage', value: 'drawPercentage', percentage: true},
  {title: 'Losses (Last Month)', value: 'losses1Month', percentage: false},
  {title: 'Loss Percentage (Last Month)', value: 'lossPercentage1Month', percentage: true},
  {title: 'Draws (Last Month)', value: 'draws1Month', percentage: false},
  {title: 'Draw Percentage (Last Month)', value: 'drawPercentage1Month', percentage: true},
  {title: 'Losses (Last 3 Months)', value: 'losses3Months', percentage: false},
  {title: 'Loss Percentage (Last 3 Months)', value: 'lossPercentage3Months', percentage: true},
  {title: 'Draws (Last 3 Months)', value: 'draws3Months', percentage: false},
  {title: 'Draw Percentage (Last 3 Months)', value: 'drawPercentage3Months', percentage: true},
  {title: 'Losses (Last 6 Months)', value: 'losses6Months', percentage: false},
  {title: 'Loss Percentage (Last 6 Months)', value: 'lossPercentage6Months', percentage: true},
  {title: 'Draws (Last 6 Months)', value: 'draws6Months', percentage: false},
  {title: 'Draw Percentage (Last 6 Months)', value: 'drawPercentage6Months', percentage: true},
  {title: 'Losses (Last 12 Months)', value: 'losses12Months', percentage: false},
  {title: 'Loss Percentage (Last 12 Months)', value: 'lossPercentage12Months', percentage: true},
  {title: 'Draws (Last 12 Months)', value: 'draws12Months', percentage: false},
  {title: 'Draw Percentage (Last 12 Months)', value: 'drawPercentage12Months', percentage: true},
]);

const derivedStats = computed(() => {
  if (!props.stats?.length) return [];
  const metricObject = metricOptions.value.find(o => o.value === selectedMetric.value);
  return [...props.stats]
      .map(stat => {
        const value = stat[selectedMetric.value];
        return {
          id: stat.id,
          player: stat.player,
          metric: value,
          output: !value
              ? "-"
              : (metricObject?.prefix || '') + (metricObject?.percentage ? doubleToPercentage(value) : value),
        };
      })
      .sort((a, b) => (sortDirection.value === 'Ascending' ? a.metric - b.metric : b.metric - a.metric));
});

// Bar rows: share of the largest |value| → brand fill.
const maxMetric = computed(() =>
    derivedStats.value.reduce((m, s) => Math.max(m, Math.abs(Number(s.metric) || 0)), 0),
);
const barShare = (stat) => {
  const v = Math.abs(Number(stat.metric) || 0);
  return maxMetric.value > 0 ? Math.round((v / maxMetric.value) * 100) : 0;
};

const toggleDirection = () => {
  sortDirection.value = sortDirection.value === 'Descending' ? 'Ascending' : 'Descending';
};
</script>

<template>
  <div class="sv">
    <!-- ===== Form (recent with chips / 2x2 grid, or all-time) ===== -->
    <section class="sv-block">
      <div class="tl-section-head" style="margin-top: 0;"><h2>{{ isAllTime ? 'Form' : 'Recent form' }}</h2></div>

      <!-- all-time: one full-width table -->
      <stats-form-table v-if="isAllTime" :rows="allTimeData" empty-text="No results yet"/>

      <template v-else>
        <!-- mobile: period chips + single full-width table -->
        <div class="d-md-none">
          <div class="period-chips">
            <button
                v-for="chip in periods"
                :key="chip.key"
                type="button"
                class="period-chip"
                :class="{ active: period === chip.key }"
                @click="period = chip.key"
            >{{ chip.label }}</button>
          </div>
          <stats-form-table :rows="activePeriod.rows" empty-text="No results in this period yet"/>
        </div>

        <!-- md+: all four periods as a 2x2 grid, no chips -->
        <v-row class="d-none d-md-flex">
          <v-col v-for="p in periods" :key="p.key" cols="12" md="6">
            <div class="sv-grid-label">{{ p.title }}</div>
            <stats-form-table :rows="p.rows" empty-text="No results in this period yet"/>
          </v-col>
        </v-row>
      </template>
    </section>

    <!-- ===== All stats (full width) ===== -->
    <section class="sv-block">
      <div class="tl-section-head"><h2>All stats</h2></div>

      <div class="sort-row">
        <v-select
            v-model="selectedMetric"
            :items="metricOptions"
            item-title="title"
            item-value="value"
            label="Sort by"
            prepend-inner-icon="mdi-sort"
            density="comfortable"
            hide-details
            class="sort-select"
        ></v-select>
        <button
            type="button"
            class="sort-dir"
            :aria-label="sortDirection === 'Descending' ? 'Sort descending' : 'Sort ascending'"
            @click="toggleDirection"
        >
          <v-icon size="20">{{ sortDirection === 'Descending' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
        </button>
      </div>

      <div class="tl-card bar-card">
        <div v-if="!derivedStats.length" class="bar-empty">No stats yet</div>
        <div v-for="stat in derivedStats" :key="stat.id + 'bar'" class="bar-row">
          <div class="bar-label"><player-card :player="stat.player"/></div>
          <div class="bar-track"><div class="bar-fill" :style="{ width: barShare(stat) + '%' }"></div></div>
          <div class="bar-value">{{ stat.output }}</div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.sv-block + .sv-block {
  margin-top: 28px;
}

/* ── Period chips (mobile only) ── */
.period-chips {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.period-chip {
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  font-size: 12px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));

  &.active {
    background: rgb(var(--v-theme-brandTint));
    border-color: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-primary));
  }
}

/* ── 2x2 grid period heading ── */
.sv-grid-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 8px;
}

/* ── Sort row ── */
.sort-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  max-width: 460px;
}

.sort-select {
  flex: 1;
  min-width: 0;
}

.sort-dir {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-text));

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

/* ── Bar rows ── */
.bar-card {
  padding: 8px 0;
}

.bar-empty {
  padding: 16px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
}

.bar-label {
  width: 160px;
  flex-shrink: 0;
  min-width: 0;
}

.bar-track {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: rgb(var(--v-theme-light));
  overflow: hidden;
}

.bar-fill {
  height: 8px;
  border-radius: 4px;
  background: rgb(var(--v-theme-primary));
}

.bar-value {
  width: 56px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
}

.bar-label :deep(.team-card-name--small) {
  font-size: 12.5px;
}
</style>
